import React, { useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';

import { AddButton } from 'components/Button';
import { Search } from 'components/Search';

import { useLevelStructures } from '../api/getLevelStructures';
import { useDeleteLevelStructure } from '../api/deleteLevelStructure';
import { useReorderLevelStructures } from '../api/reorderLevelStructures';
import { useDragOrder } from '../hooks/useDragOrder';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { LevelStructureFormModal } from './LevelStructureFormModal';
import { LevelStructureListItem } from './LevelStructureListItem';
import { LevelsView } from './LevelsView';
import { SetupOverlay } from './SetupOverlay';

export const LevelStructuresOverlay = ({ resource, id, procurementName, onClose }) => {
  const [editedStructure, setEditedStructure] = useState(null);
  const [deletedStructure, setDeletedStructure] = useState(null);
  const [openStructureId, setOpenStructureId] = useState(null);
  const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const isModalOpen = !!editedStructure || !!deletedStructure || isLevelModalOpen;

  const { data: structures, refetch, isFetchedAfterMount } = useLevelStructures(resource, id);
  const { mutateAsync: deleteStructure } = useDeleteLevelStructure(resource, id, () => {
    refetch();
    setDeletedStructure(null);
  });
  const { mutateAsync: reorderStructures } = useReorderLevelStructures(resource, id, refetch);
  const { order, draggedId, dragHandlers } = useDragOrder(
    structures,
    'level_structure_id',
    reorderStructures
  );

  const openStructure = structures.find(
    ({ level_structure_id }) => level_structure_id === openStructureId
  );

  const crumbs = openStructure
    ? [
        { label: 'Level structures', onClick: () => setOpenStructureId(null) },
        { label: openStructure.name },
        { label: 'Levels' },
      ]
    : [{ label: 'Level structures' }];

  const searchTerm = search.trim().toLowerCase();
  const filteredStructures = order.filter(({ name, levels }) =>
    [name, ...levels.map((level) => level.name)].some((value) =>
      value.toLowerCase().includes(searchTerm)
    )
  );
  const canDrag = !searchTerm && order.length > 1;

  return (
    <SetupOverlay
      procurementName={procurementName}
      crumbs={crumbs}
      isModalOpen={isModalOpen}
      onClose={onClose}
    >
      {openStructure ? (
        <LevelsView
          resource={resource}
          id={id}
          structure={openStructure}
          onChanged={refetch}
          onModalChange={setIsLevelModalOpen}
        />
      ) : (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Search
              handleSearchChange={({ target: { value } }) => setSearch(value)}
              sx={{ minWidth: 200, width: 270 }}
            />
            <AddButton
              onClick={() => setEditedStructure({})}
              sx={({ palette }) => ({
                svg: { mt: -0.5 },
                minWidth: 200,
                border: `1px solid ${palette.grey[200]}`,
              })}
              color="secondary"
            >
              Add level structure
            </AddButton>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {isFetchedAfterMount && filteredStructures.length === 0 && (
            <Stack flex={1} alignItems="center" justifyContent="center" gap={1}>
              <Typography variant="h5" fontWeight={700}>
                {searchTerm ? 'No level structures found' : 'No level structures yet'}
              </Typography>
              <Typography color="text.secondary">
                {searchTerm
                  ? 'Please try another search term'
                  : 'Click "Add level structure" to begin'}
              </Typography>
            </Stack>
          )}

          <Stack gap={1}>
            {filteredStructures.map((structure) => (
              <LevelStructureListItem
                key={structure.level_structure_id}
                canDrag={canDrag}
                isDragged={draggedId === structure.level_structure_id}
                dragHandlers={dragHandlers(structure.level_structure_id)}
                openLevels={() => setOpenStructureId(structure.level_structure_id)}
                markForEdit={() => setEditedStructure(structure)}
                markForDelete={
                  structure.role_count ? undefined : () => setDeletedStructure(structure)
                }
                {...structure}
              />
            ))}
          </Stack>
        </>
      )}

      <LevelStructureFormModal
        resource={resource}
        id={id}
        structure={editedStructure}
        open={!!editedStructure}
        handleClose={() => setEditedStructure(null)}
        onSaved={refetch}
      />

      <DeleteConfirmModal
        title="Delete level structure"
        label="level structure"
        name={deletedStructure?.name}
        open={!!deletedStructure}
        handleClose={() => setDeletedStructure(null)}
        onConfirm={() => deleteStructure(deletedStructure.level_structure_id).catch(() => {})}
      />
    </SetupOverlay>
  );
};
